<script setup lang="ts">
import { logger } from '@pkg/logger';
import { THEME } from '@pkg/config/misc/theme';
import type { Signature, SignatureGeneratorFragment, SignaturePronouns } from '@pkg/types/signatureGenerator';
import { markets } from '@pkg/config/languages';
import { LOCALE_CODE } from '@pkg/config/languages/localeCodes';
import { useStorage } from '@vueuse/core';
import { FormFieldText, FormFieldSelect, FormFieldCheckboxes } from './form-fields';
import { useSignatureGenerator } from '~/app/composables/useSignatureGenerator';
import { useLocale } from '~/app/composables/useLocale';

const props = defineProps<{
  theme?: THEME,
  signatureGeneratorFragment: SignatureGeneratorFragment,
  defaultPronouns: SignaturePronouns[],
}>();

const SIGNATURE_LOGO_URL = 'https://static-mkt.toogoodtogo.com/email-signature/logo-animated-68x53.gif';
const SIGNATURE_FONT = 'font-family: \'Google Sans Flex\', \'Helvetica Neue\', Helvetica, Arial, sans-serif;';
const SIGNATURE_BODY = 'font-size: 11px; line-height: 16px; color: #6d7370; font-weight: 400;';
const SIGNATURE_FONT_IMPORT = '@import url(\'https://fonts.googleapis.com/css2?family=Google+Sans+Flex:wght@400;700&display=swap\');';

const formValues = ref({
  country: LOCALE_CODE.EN_GB,
  pronoun: '',
  firstName: '',
  lastName: '',
  email: '',
  jobTitle: '',
  phone: '',
  officePhone: '',
});

const showSignature = ref(false);
const signature = ref();
const officeAddress = ref(['']);
const privacyPolicy = ref('');
const phoneNumber = ref('');
const extraAddressText = ref(['']);
const officePhoneNumber = ref('');
const copyMessage = ref();
const { locale } = useI18n();
const { form, getCMSTexts, getCMSPronounsList } = useSignatureGenerator();
const errorText = ref(false);
const checkboxOptionsArray = [{ title: 'Show office phone', value: 'true' }];
const { options } = useLocale();
const getFlag = (country: string) => Object.values(markets)?.filter(market => market.country === country)[0].icon;
const localesList = options?.map(country => country.locales.map(language => ({ value: language.localeCode, title: `${getFlag(country.country)} ${country.country} (${language.languageName})` }))).flat();
const defaultCountryOption = localesList.find(country => country.value === locale.value as LOCALE_CODE);

const parsePronouns = (pronouns: any) => {
  const pronounsArray = pronouns?.map((pronoun: any) => ({ title: pronoun.title, value: pronoun.title }));
  const finalPronounsArray = pronounsArray?.length && [...pronounsArray, { title: 'none', value: 'none' }];
  return finalPronounsArray || [];
};
const pronounsList = ref(parsePronouns(props.signatureGeneratorFragment?.pronouns));
const currentPronoun = ref(formValues.value.pronoun);
const parsedPronoun = computed(() => (!!currentPronoun.value && currentPronoun.value !== 'none') ? `(${currentPronoun.value})` : '');
const parseDefaultPronouns = computed(() => parsePronouns(props.defaultPronouns));

const onCopy = () => {
  try {
    const content = signature.value.innerHTML;
    const blobInput = new Blob([content as BlobPart], { type: 'text/html' });
    const clipboardItemInput = new ClipboardItem({ 'text/html': blobInput });
    navigator.clipboard.write([clipboardItemInput]);
    copyMessage.value = 'Copied!';
  } catch (e) {
    copyMessage.value = 'We couldn\'t copy the signature. Please do it with the mouse.';
  }
};

const getResultTexts = async(locale: LOCALE_CODE) => {
  const { officeAddressText, addressExtraContent, privacyPolicyText, officePhoneText } = await getCMSTexts(locale);
  officeAddress.value = officeAddressText;
  extraAddressText.value = addressExtraContent;
  privacyPolicy.value = privacyPolicyText;
  officePhoneNumber.value = officePhoneText;

  errorText.value = officeAddress.value[0] === '';
};

const getPhoneNumber = (values: Signature) => {
  const isThereDirectPhone = values.phone !== undefined && values.phone !== '';
  const showOfficePhone = values?.officePhone && values?.officePhone[0];

  if (isThereDirectPhone) {
    return values.phone;
  } else if (!isThereDirectPhone && showOfficePhone) {
    return officePhoneNumber.value;
  } return '';
};
const selectedCountry = ref(defaultCountryOption?.value);

const onSubmit = form.handleSubmit(async(values: {}) => {
  try {
    errorText.value = false;
    formValues.value = values as Signature;
    formValues.value.country = selectedCountry.value || LOCALE_CODE.EN_GB;
    formValues.value.pronoun = currentPronoun.value;
    await getResultTexts(selectedCountry.value || LOCALE_CODE.EN_GB);
    phoneNumber.value = getPhoneNumber(formValues.value);
    showSignature.value = true;
    return true;
  } catch (error) {
    logger.error('Signature form error', error);
    errorText.value = true;
  }
});

let amplitudeForm = ref();

onMounted(() => {
  amplitudeForm = useStorage('amplitudeForm', {
    formName: 'internal',
    formId: 'signatureGenerator',
  });
});

onUnmounted(() => {
  amplitudeForm.value = null;
});

const updatePronounsList = async(newSelectedCountry?: LOCALE_CODE) => {
  currentPronoun.value = '';
  const newTranslatedPronouns = await getCMSPronounsList(newSelectedCountry || LOCALE_CODE.EN_GB);
  if (newTranslatedPronouns.pronouns) {
    pronounsList.value = parsePronouns(newTranslatedPronouns.pronouns);
  } else {
    pronounsList.value = parseDefaultPronouns.value;
  }
};

watch(selectedCountry, async(newValue) => {
  showSignature.value = false;
  await updatePronounsList(newValue);
});

</script>

<template>
  <PartialSection :theme="props.theme || THEME.DARK">
    <div class="c-section-block__wrapper wrapper">
      <div class="c-section-block__content c-section-block__content--inner content-container flex flex-col justify-items-center items-center">
        <BaseTitle tag="h3" class="mt-16 mb-16 text-center" title="Signature generator" />
        <div class="wrapper wrapper--content">
          <form @submit="onSubmit">
            <div class="mb-8">
              <FormFieldSelect v-model="selectedCountry" name="country" label="Country" :options="localesList" :default-value="defaultCountryOption?.value" />
            </div>
            <div class="mb-8">
              <FormFieldSelect v-model="currentPronoun" name="pronoun" label="Pronouns" :options="pronounsList" />
            </div>
            <div class="mb-8">
              <FormFieldText name="firstName" label="First name" />
            </div>
            <div class="mb-8">
              <FormFieldText name="lastName" label="Last name" />
            </div>
            <div class="mb-8">
              <FormFieldText name="jobTitle" label="Job title" />
            </div>
            <div class="mb-8">
              <FormFieldText name="email" label="Email address" />
            </div>
            <div class="mb-8">
              <FormFieldText name="phone" label="Direct number (optional)" />
              <p style="font-size: small; margin-top: 8px;">
                Your direct number will replace office number
              </p>
            </div>
            <div class="mb-8">
              <FormFieldCheckboxes id="officePhone" name="officePhone" :options="checkboxOptionsArray" />
            </div>
            <UiButton type="submit" :theme="props.theme" class="mt-16">
              Generate signature
            </UiButton>
          </form>

          <div v-if="errorText" style="margin-top: 20px;">
            <p>There was an error generating the signature. Please try again or contact the Web Team.</p>
          </div>

          <div
            v-if="showSignature && !errorText"
            id="signature"
            class="signature-wrapper"
          >
            <div style=" margin-bottom: 20px;">
              <UiButton :theme="props.theme" class="mt-16" @click="onCopy">
                Copy signature
              </UiButton>
              <p v-if="copyMessage" style="padding-top: 10px; max-width: 70%">
                {{ copyMessage }}
              </p>
            </div>

            <div ref="signature" style="background-color: #ffffff; padding: 20px;">
              <component :is="'style'">
                {{ SIGNATURE_FONT_IMPORT }}
              </component>
              <table cellpadding="0" cellspacing="0" border="0" style="border-collapse: collapse;">
                <tbody>
                  <tr>
                    <td style="padding: 0 20px 0 0; vertical-align: middle;">
                      <img :src="SIGNATURE_LOGO_URL" width="68" height="53" alt="Too Good To Go" style="display: block; width: 68px; height: 53px; border: 0; outline: none; text-decoration: none;">
                    </td>

                    <td style="padding: 0; vertical-align: middle;">
                      <div :style="`${SIGNATURE_FONT} font-size: 16px; line-height: 21px; color: #124f30; font-weight: 700;`">
                        {{ formValues.firstName }} {{ formValues.lastName }} {{ parsedPronoun }}
                      </div>

                      <div :style="`${SIGNATURE_FONT} font-size: 11px; line-height: 16px; color: #6d7370; font-weight: 700; text-transform: uppercase; letter-spacing: 0.08em; margin-bottom: 12px;`">
                        {{ formValues.jobTitle }}
                      </div>

                      <div :style="`${SIGNATURE_FONT} ${SIGNATURE_BODY}`">
                        <template v-if="phoneNumber !== ''">
                          <a :href="`tel:${phoneNumber}`" style="color: #6d7370; text-decoration: none;">{{ phoneNumber }}</a>
                          &nbsp;|&nbsp;
                        </template>
                        <a :href="`mailto:${formValues.email}`" style="color: #6d7370; text-decoration: none;">{{ formValues.email }}</a>
                      </div>

                      <div v-for="text, index in officeAddress" :key="index" :style="`${SIGNATURE_FONT} ${SIGNATURE_BODY}`">
                        {{ text }}
                      </div>

                      <div :style="`${SIGNATURE_FONT} ${SIGNATURE_BODY}`">
                        <a href="https://www.toogoodtogo.com" style="color: #6d7370; text-decoration: none;">toogoodtogo.com</a>
                        &nbsp;|&nbsp;
                        <a href="https://www.toogoodtogo.com/privacy" style="color: #6d7370; text-decoration: none;">{{ privacyPolicy }}</a>
                      </div>
                    </td>
                  </tr>
                </tbody>
              </table>

              <div v-if="extraAddressText?.length > 0" :style="`${SIGNATURE_FONT} font-size: 9px; line-height: 14px; color: #6d7370; font-weight: 400; padding-top: 16px;`">
                <p v-for="text, index in extraAddressText" :key="index">
                  {{ text }}
                </p>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  </PartialSection>
</template>

<style scoped>
  .signature-wrapper {
    width: 100%;
    margin-top: 32px; display: flex;
    flex-direction: column;
    position: relative;
    justify-content: space-evenly;
    align-items: center;

    @screen tablet-wide {
      flex-direction: row;
    }
  }

  </style>
